import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './SocialMediaPage.module.css';

export const SocialMediaPageComponent = props => {
  const { scrollingDisabled, pageAssetsData, inProgress } = props;

  const title = 'Social Media Links';
  const description = 'Follow us on social media';

  // Get social media data from CMS
  const socialMediaAsset = pageAssetsData?.['social-media'];
  const socialMediaData = socialMediaAsset?.data;
  const sections = socialMediaData?.data?.sections || socialMediaData?.sections || [];

  // Find the first section (should be the social media section)
  const socialMediaSection = sections[0];
  const blocks = socialMediaSection?.blocks || [];

  // Group blocks into pairs (title + link/image)
  const socialMediaPairs = [];
  for (let i = 0; i < blocks.length; i += 2) {
    if (blocks[i]) {
      const titleBlock = blocks[i];
      const imageBlock = blocks[i + 1];

      // Get image URL - check multiple possible paths
      let imageUrl = '';

      if (imageBlock?.media?.fieldType === 'image') {
        // The CMS data has already been processed and the included assets have been merged
        // into the blocks, so we can directly access attributes from the image object
        if (imageBlock?.media?.image?.attributes?.variants) {
          const variants = imageBlock.media.image.attributes.variants;

          // Try different variant names (landscape* or original*)
          imageUrl = variants.landscape800?.url ||
                     variants.landscape1200?.url ||
                     variants.landscape400?.url ||
                     variants.landscape2400?.url ||
                     variants.original800?.url ||
                     variants.original1200?.url ||
                     variants.original400?.url ||
                     variants.original2400?.url || '';
        }
      }

      // Get link - prioritize image block link, then check if it's an externalImageLink
      let linkUrl = '#';
      if (imageBlock?.media?.link?.fieldType === 'externalImageLink' && imageBlock?.media?.link?.href) {
        linkUrl = imageBlock.media.link.href;
      } else if (imageBlock?.media?.link?.href) {
        linkUrl = imageBlock.media.link.href;
      }

      socialMediaPairs.push({
        title: titleBlock.title?.content || '',
        description: titleBlock.text?.content || '',
        link: linkUrl,
        image: imageUrl,
        imageAlt: imageBlock?.media?.alt || titleBlock.title?.content || '',
        linkText: imageBlock?.text?.content || '',
      });
    }
  }

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.layoutWrapperMain}
      >
        <div className={css.root}>
          {/* Hero Section */}
          <div className={css.heroSection}>
            <h1 className={css.pageTitle}>Social Media Links</h1>
          </div>

          {/* Social Media Grid */}
          <div className={css.socialMediaSection}>
            {inProgress ? (
              <div className={css.loading}>Loading...</div>
            ) : (
              <div className={css.socialMediaGrid}>
                {socialMediaPairs.map((pair, index) => (
                  <div key={index} className={css.socialMediaPair}>
                    {/* Title */}
                    <div className={css.socialMediaTitle}>
                      <h2>{pair.title}</h2>
                      {pair.description && <p>{pair.description}</p>}
                    </div>

                    {/* Link with Image */}
                    <div className={css.socialMediaLink}>
                      <a href={pair.link} target="_blank" rel="noopener noreferrer">
                        {pair.image && (
                          <img src={pair.image} alt={pair.imageAlt} className={css.socialImage} />
                        )}
                        {pair.linkText && <p className={css.linkText}>{pair.linkText}</p>}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SocialMediaPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  pageAssetsData: object,
  inProgress: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress } = state.hostedAssets || {};
  return {
    scrollingDisabled: state.ui?.scrollingDisabled || false,
    pageAssetsData,
    inProgress,
  };
};

const SocialMediaPage = compose(connect(mapStateToProps))(SocialMediaPageComponent);

export default SocialMediaPage;
